import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DataStatus = 'live' | 'partial' | 'unavailable' | 'loading';

interface DataStatusBadgeProps {
  status: DataStatus;
  label?: string;
  className?: string;
}

const statusConfig: Record<DataStatus, { 
  icon: React.ElementType; 
  label: string; 
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
}> = {
  live: { 
    icon: CheckCircle2, 
    label: 'Live Data', 
    variant: 'default',
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20'
  },
  partial: { 
    icon: AlertCircle, 
    label: 'Partial Data', 
    variant: 'secondary',
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20'
  },
  unavailable: { 
    icon: XCircle, 
    label: 'No Data', 
    variant: 'destructive',
    className: 'bg-destructive/10 text-destructive border-destructive/20'
  },
  loading: { 
    icon: Loader2, 
    label: 'Loading...', 
    variant: 'outline',
    className: 'bg-muted/50 text-muted-foreground border-muted'
  }
};

export const DataStatusBadge: React.FC<DataStatusBadgeProps> = ({ 
  status, 
  label,
  className 
}) => {
  const config = statusConfig[status];
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        'gap-1 text-xs font-medium',
        config.className,
        className
      )}
    >
      <Icon className={cn('h-3 w-3', status === 'loading' && 'animate-spin')} />
      {label || config.label}
    </Badge>
  );
};

export default DataStatusBadge;
